import React, { useState, useEffect } from 'react'; // eslint-disable-line
import axios from 'axios';
import { api, API_URL, API_KEY, downloadCSV, Badge, Pagination, paginate, PAGE_SIZE, SearchBox, searchFilter } from './shared';

const TeamsTab = ({ onError, sortMode = 'default', canManage = false }) => {
  const [teams, setTeams] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  // รายละเอียดทีม
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconMsg, setReconMsg] = useState(null);

  const runReconcile = async () => {
    if (!window.confirm('รัน Reconcile กับ CLM?\n\nระบบจะเช็คทุกบิลกับ CLM:\n- บิลปกติ → ไม่กระทบคะแนน\n- บิลที่ลูกค้าคืน → หักคะแนนคืน\n\nใช้เวลา 1-3 นาที')) return;
    setReconLoading(true); setReconMsg(null);
    try {
      await axios.post(`${API_URL}/admin/bts/reconcile/run`, {}, { headers: { 'x-api-key': API_KEY }, timeout: 10000 });
      setReconMsg('✅ เริ่ม Reconcile แล้ว — รอ 1-3 นาที แล้วรีเฟรชดูผล');
    } catch(e) {
      setReconMsg('❌ ' + (e.response?.data?.message || e.message));
    } finally { setReconLoading(false); }
  };

  const fetch = async () => { setLoading(true); try { const r = await api('teams'); setTeams(r.data.data); setSummary(r.data.summary); } catch(e) { onError(e.message); } finally { setLoading(false); } };
  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    try {
      const r = await api(`team/${id}`);
      setDetail(r.data);
    } catch(e) { onError(e.message); }
    finally { setDetailLoading(false); }
  };

  const byType = teams ? (
    filter === 'all' ? teams :
    filter === 'complete' ? teams.filter(t => t.member_count >= 4) :
    filter === 'ranked' ? teams.filter(t => t.member_count >= 4 && t.total_tickets > 0) :
    filter === 'incomplete' ? teams.filter(t => t.member_count < 4) : teams
  ) : [];
  // sort mode 'memberThenSpend' = ทีมครบ 4 ก่อน, แล้วเรียงตามยอด
  const sorted = sortMode === 'memberThenSpend'
    ? [...byType].sort((a, b) => (b.member_count - a.member_count) || (parseFloat(b.total_tickets || 0) - parseFloat(a.total_tickets || 0)))
    : byType;
  const filtered = searchFilter(sorted, search, ['party_name']);

  // ถ้ากำลังดูรายละเอียดทีม
  if (detail) {
    const { team, members, receipts } = detail;
    return (
      <div className="bts-admin__section">
        <div className="bts-admin__topbar">
          <div className="bts-admin__page-title">
            <button onClick={() => setDetail(null)} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', marginRight:8 }}>←</button>
            🏆 {team.party_name}
          </div>
        </div>

        {/* ข้อมูลทีม */}
        <div style={{ background:'#f8f8ff', borderRadius:12, padding:16, marginBottom:16, border:'1px solid #e0d4f5' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:8, fontSize:13 }}>
            <div><span style={{ color:'#888' }}>รหัสทีม:</span> <b>{team.party_code}</b></div>
            <div><span style={{ color:'#888' }}>สมาชิก:</span> <b>{team.member_count}/4</b></div>
            <div><span style={{ color:'#888' }}>คะแนนรวม:</span> <b>{team.total_tickets}</b></div>
            <div><span style={{ color:'#888' }}>สถานะ:</span> {team.status === 'completed' ? <Badge color="green">ครบ</Badge> : <Badge color="orange">{team.status}</Badge>}</div>
            <div><span style={{ color:'#888' }}>สร้างเมื่อ:</span> <b>{new Date(team.created_at).toLocaleString('th-TH')}</b></div>
            <div><span style={{ color:'#888' }}>บิลทั้งหมด:</span> <b>{receipts.length}</b></div>
          </div>
        </div>

        {/* สมาชิก */}
        <p style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>สมาชิก ({members.length} คน)</p>
        <div className="bts-admin__table-wrap" style={{ marginBottom:16 }}>
          <table className="bts-admin__table">
            <thead><tr><th>#</th><th>ชื่อ</th><th>BigPoint</th><th>CusId (ระบบ)</th><th>คะแนน</th><th>เจ้าของ</th><th>New Member</th><th>เข้าร่วม</th></tr></thead>
            <tbody>{members.map((m, i) => (
              <tr key={i}>
                <td>{m.join_position}</td>
                <td>{m.display_name}</td>
                <td>{m.bigpoint_id || '-'}</td>
                <td>{m.clmCusId || '-'}</td>
                <td><b>{m.personal_tickets}</b></td>
                <td>{m.is_owner ? '👑' : ''}</td>
                <td>{m.is_new_member ? <Badge color="green">x2</Badge> : ''}</td>
                <td>{m.joined_at ? new Date(m.joined_at).toLocaleString('th-TH') : '-'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>

        {/* บิล */}
        <p style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>บิลที่สแกน ({receipts.length} บิล | ฿{receipts.filter(r=>r.status==='active').reduce((s,r)=>s+Number(r.amount||0),0).toLocaleString()})</p>
        <div className="bts-admin__table-wrap">
          <table className="bts-admin__table">
            <thead><tr><th>สถานะ</th><th>ชื่อ</th><th>BigPoint</th><th>CusId (ระบบ)</th><th>CustomerID</th><th>เลขบิล</th><th>Partner</th><th>สาขา</th><th>ยอด</th><th>คะแนน</th><th>ตัวคูณ</th><th>สแกนเมื่อ</th></tr></thead>
            <tbody>{receipts.map((r, i) => (
              <tr key={i} className={r.status==='reverted'?'':'bts-admin__row--ok'}>
                <td>{r.status==='reverted'?<Badge color="gray">{r.statusLabel}</Badge>:<Badge color="green">{r.statusLabel}</Badge>}</td>
                <td>{r.displayName}</td>
                <td>{r.bigpointId || '-'}</td>
                <td>{r.clmCusId || '-'}</td>
                <td>{r.customerId}</td>
                <td>{r.receiptNo}</td>
                <td>{r.partner}</td>
                <td>{r.store}</td>
                <td>฿{Number(r.amount||0).toLocaleString()}</td>
                <td><b>{r.status==='reverted'?<span style={{color:'#E24B4A'}}>-{r.tickets}</span>:r.tickets}</b></td>
                <td>{r.multiplier > 1 ? <Badge color="green">x{r.multiplier}</Badge> : 'x1'}</td>
                <td style={{ whiteSpace:'nowrap' }}>{new Date(r.scannedAt).toLocaleString('th-TH')}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    );
  }

  // ตารางทีมทั้งหมด
  return (
    <div className="bts-admin__section">
      <div className="bts-admin__topbar">
        <div className="bts-admin__page-title">🏆 ทีมทั้งหมด</div>
        <div className="bts-admin__actions">
          <button className="bts-admin__btn bts-admin__btn--dark" onClick={fetch} disabled={loading}>{loading ? 'โหลด...' : 'รีเฟรช'}</button>
          {canManage && <button className="bts-admin__btn bts-admin__btn--blue" onClick={runReconcile} disabled={reconLoading}>{reconLoading ? 'กำลังรัน...' : '🔄 รัน Reconcile'}</button>}
          {canManage && teams && <button className="bts-admin__btn bts-admin__btn--green bts-admin__btn--sm" onClick={() => downloadCSV(
            ['อันดับ','ชื่อทีม','สมาชิก','คะแนนรวม','สถานะ','สร้างเมื่อ'],
            teams.map((t,i) => [i+1, t.party_name, t.member_count+'/4', t.total_tickets, t.status, new Date(t.created_at).toLocaleString('th-TH')]),
            'bts_teams.csv'
          )}>Export CSV</button>}
        </div>
      </div>
      {reconMsg && <div className={`bts-admin__alert ${reconMsg.startsWith('✅') ? 'bts-admin__alert--success' : 'bts-admin__alert--error'}`}>{reconMsg} <button onClick={() => setReconMsg(null)} style={{ marginLeft: 8, border: 'none', background: 'none', cursor: 'pointer' }}>✕</button></div>}
      {loading && <p>กำลังโหลด...</p>}
      {detailLoading && <p>กำลังโหลดรายละเอียดทีม...</p>}
      {summary && (<>
        <div className="bts-admin__cards">
          <div className={`bts-admin__card ${filter==='all'?'bts-admin__card--active':''}`} style={{ borderColor:'#333' }} onClick={() => { setFilter('all'); setPage(1); }}><div className="bts-admin__card-value" style={{ color:'#333' }}>{summary.total}</div><div className="bts-admin__card-label">ทีมทั้งหมด</div></div>
          <div className={`bts-admin__card ${filter==='complete'?'bts-admin__card--active':''}`} style={{ borderColor:'#10C851' }} onClick={() => { setFilter('complete'); setPage(1); }}><div className="bts-admin__card-value" style={{ color:'#10C851' }}>{summary.complete}</div><div className="bts-admin__card-label">ครบ 4 คน</div></div>
          <div className={`bts-admin__card ${filter==='ranked'?'bts-admin__card--active':''}`} style={{ borderColor:'#459efc' }} onClick={() => { setFilter('ranked'); setPage(1); }}><div className="bts-admin__card-value" style={{ color:'#459efc' }}>{summary.ranked}</div><div className="bts-admin__card-label">ถูกจัดอันดับ</div></div>
          <div className={`bts-admin__card ${filter==='incomplete'?'bts-admin__card--active':''}`} style={{ borderColor:'#FF8A00' }} onClick={() => { setFilter('incomplete'); setPage(1); }}><div className="bts-admin__card-value" style={{ color:'#FF8A00' }}>{summary.total - summary.complete}</div><div className="bts-admin__card-label">ยังไม่ครบ</div></div>
        </div>
        <div className="bts-admin__case-box">
          <div className="bts-admin__case-title">สรุปกรณี</div>
          <div className="bts-admin__case-row" onClick={() => { setFilter('complete'); setPage(1); }}><span>✅ ทีมครบ 4 คน</span><span className="bts-admin__case-count">{summary.complete} ทีม</span></div>
          <div className="bts-admin__case-row" onClick={() => { setFilter('ranked'); setPage(1); }}><span>🏆 ทีมถูกจัดอันดับ (ครบ + มีคะแนน)</span><span className="bts-admin__case-count">{summary.ranked} ทีม</span></div>
          <div className="bts-admin__case-row" onClick={() => { setFilter('incomplete'); setPage(1); }}><span>🕐 ทีมยังไม่ครบ 4 คน</span><span className="bts-admin__case-count">{summary.total - summary.complete} ทีม</span></div>
        </div>
      </>)}
      {teams && (<>
        <div className="bts-admin__filter-info"><span>แสดง {filtered.length} ทีม</span><SearchBox value={search} onChange={v => { setSearch(v); setPage(1); }} /></div>
        <div className="bts-admin__table-wrap">
          <table className="bts-admin__table">
            <thead><tr><th>อันดับ</th><th>ชื่อทีม</th><th>สมาชิก</th><th>คะแนนรวม</th><th>สถานะ</th><th>สร้างเมื่อ</th></tr></thead>
            <tbody>{paginate(filtered, page).map((t, i) => (
              <tr key={i} className={t.member_count >= 4 && t.total_tickets > 0 ? 'bts-admin__row--ok' : ''} onClick={() => fetchDetail(t.id)} style={{ cursor:'pointer' }}>
                <td><b>{(page - 1) * PAGE_SIZE + i + 1}</b></td>
                <td style={{ color:'#459efc', textDecoration:'underline' }}>{t.party_name}</td>
                <td>{t.member_count}/4</td><td><b>{t.total_tickets}</b></td>
                <td>{t.status === 'completed' ? <Badge color="green">ครบ</Badge> : <Badge color="orange">{t.status}</Badge>}</td>
                <td>{new Date(t.created_at).toLocaleString('th-TH')}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <Pagination total={filtered.length} page={page} setPage={setPage} />
      </>)}
    </div>
  );
};

export default TeamsTab;
